import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { RouterStateSnapshot, TitleStrategy } from "@angular/router";

const SECTION_TITLES: Record<string, string> = {
  "freshbooks/dashboard": "Freshbooks dashboard",
  "freshbooks/invoices": "Invoices",
  "radio-scanners": "Radio scanners",
  "rdio-profiles": "Rdio profiles",
  "openmhz-profiles": "OpenMHz profiles",
  "vtext-profiles": "VText profiles",
  members: "Members",
  "bnn-roles": "BNN roles",
  incidents: "Incidents",
  templates: "Templates",
  "settings/users": "Users",
  "settings/settings": "Settings",
};

const SINGULAR_TITLES: Record<string, string> = {
  "radio-scanners": "radio scanner",
  "rdio-profiles": "Rdio profile",
  "openmhz-profiles": "OpenMHz profile",
  "vtext-profiles": "VText profile",
  members: "member",
  "bnn-roles": "BNN role",
  incidents: "incident",
  templates: "template",
  user: "user",
};

export function getPageTitle(url: string): string {
  const path = url.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, "");
  const segments = path.split("/").filter(Boolean);

  if (!segments.length) {
    return "Freshbooks dashboard";
  }

  if (segments[0] === "auth") {
    return segments[1] === "code" ? "Verification" : "Sign in";
  }

  if (segments[0] === "templates" && segments[1] === "preview") {
    return "Template preview";
  }

  if (segments[0] === "templates" && segments[1] === "send") {
    return "Send template";
  }

  const isEdit = segments.includes("edit") || segments[1] === "user";
  if (isEdit) {
    const entityKey = segments[1] === "user" ? "user" : segments[0];
    const entity = SINGULAR_TITLES[entityKey] ?? "record";
    const id = segments.at(-1);
    return id === "0" || id === "new" ? `New ${entity}` : `Edit ${entity}`;
  }

  const twoPartKey = segments.slice(0, 2).join("/");
  return (
    SECTION_TITLES[twoPartKey] ?? SECTION_TITLES[segments[0]] ?? "BNN Admin"
  );
}

export function getBackDestination(url: string): string | null {
  const path = url.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, "");
  const segments = path.split("/").filter(Boolean);

  if (segments[1] === "edit") {
    return `/${segments[0]}/list`;
  }

  if (
    segments[0] === "templates" &&
    (segments[1] === "preview" || segments[1] === "send")
  ) {
    return "/templates/list";
  }

  if (segments[0] === "settings" && segments[1] === "user") {
    return "/settings/users";
  }

  return null;
}

@Injectable()
export class AppTitleStrategy extends TitleStrategy {
  constructor(private readonly title: Title) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const pageTitle = this.buildTitle(snapshot) ?? getPageTitle(snapshot.url);
    this.title.setTitle(`${pageTitle} — BNN Admin`);
  }
}
